/**
 * Declarative exposure and click tracking.
 *
 * Exposure:
 *   Add `.module-exposure-view`, `data-event`, and
 *   `data-event-parameters` to the module element.
 *
 * Click:
 *   Add `.custom-event-click`, `data-event`, and
 *   `data-event-parameters` to the clickable element.
 */
(function (window, document) {
  'use strict';

  var EXPOSURE_SELECTOR = '.module-exposure-view';
  var CLICK_SELECTOR = '.custom-event-click, [data-event][data-event-parameters]:not(.module-exposure-view)';
  var EXPOSURE_THRESHOLD = 0.3;
  var observedExposureElements = new WeakSet();
  var trackedExposureElements = new WeakSet();
  var exposureObserver = null;
  var mutationObserver = null;

  function parseEventParameters(element) {
    var parameters = element?.dataset?.eventParameters;
    if (!parameters) return {};

    try {
      return JSON.parse(parameters);
    } catch (error) {
      console.error('[CustomEvent] Invalid data-event-parameters JSON.', element, error);
      return null;
    }
  }

  function getTrackingParams(element) {
    var params = parseEventParameters(element);
    if (!params) return null;

    var belongAreaElement = element.closest('[data-belong-area]')
      || element.querySelector('[data-belong-area]');
    var customEventElement = element.closest('custom-event');

    params.event = element.dataset.event || params.event;
    params.page_type = element.dataset.pageType || params.page_type;
    params.event_label = element.dataset.eventLabel || params.event_label;
    params.event_action = element.dataset.eventAction || params.event_action;
    params.page_name = element.dataset.pageName
      || customEventElement?.dataset.pageName
      || params.page_name;
    params.belong_area = element.dataset.belongArea
      || belongAreaElement?.dataset.belongArea
      || params.belong_area;

    return params;
  }

  function sendTrackingEvent(element) {
    var params = getTrackingParams(element);
    if (!params?.event) return null;
    if (window.brandTracking && typeof window.brandTracking.track === 'function') {
      return window.brandTracking.track(params);
    }

    console.warn('[CustomEvent] Tracking is unavailable.');
    return null;
  }

  function trackExposureElement(element) {
    if (trackedExposureElements.has(element)) return null;

    var payload = sendTrackingEvent(element);
    if (payload) trackedExposureElements.add(element);
    return payload;
  }

  function trackClickElement(element) {
    return sendTrackingEvent(element);
  }

  function createExposureObserver() {
    if (!('IntersectionObserver' in window)) return null;

    return new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting || entry.intersectionRatio < EXPOSURE_THRESHOLD) return;

        var payload = trackExposureElement(entry.target);
        if (payload) exposureObserver.unobserve(entry.target);
      });
    }, { threshold: EXPOSURE_THRESHOLD });
  }

  function observeExposureElement(element) {
    if (!element || observedExposureElements.has(element)) return;
    observedExposureElements.add(element);

    if (!exposureObserver) {
      trackExposureElement(element);
      return;
    }

    exposureObserver.observe(element);
  }

  function observeExposureElements(root) {
    if (!root) return;

    if (root.matches?.(EXPOSURE_SELECTOR)) observeExposureElement(root);
    root.querySelectorAll?.(EXPOSURE_SELECTOR).forEach(observeExposureElement);
  }

  function onDocumentClick(event) {
    var target = event.target instanceof Element
      ? event.target.closest(CLICK_SELECTOR)
      : null;
    if (!target) return;

    trackClickElement(target);
  }

  function watchDynamicModules() {
    if (!('MutationObserver' in window) || !document.body) return;

    mutationObserver = new MutationObserver(function (records) {
      records.forEach(function (record) {
        record.addedNodes.forEach(function (node) {
          if (node instanceof Element) observeExposureElements(node);
        });
      });
    });

    mutationObserver.observe(document.body, { childList: true, subtree: true });
  }

  function init() {
    exposureObserver = createExposureObserver();
    observeExposureElements(document);
    watchDynamicModules();
    document.addEventListener('click', onDocumentClick);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  window.brandCustomEvent = window.brandCustomEvent || {};
  window.brandCustomEvent.trackClickElement = trackClickElement;
  window.brandCustomEvent.trackExposureElement = trackExposureElement;
  window.brandCustomEvent.observeExposureElements = observeExposureElements;
})(window, document);
